import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { OrderTab } from '@/components/pos/pos-types'
import type {
    PosLotAllocationInput,
    StockOutMaterialItemInput,
    StockOutPreviewRequest,
} from '@/lib/api/sales-order-api'
import type {
    LotLine,
    MatLine,
} from '@/components/sections/lot-allocation-editor'
import {
    hasAnyError,
    hasMatError,
    lineShortfall,
    matLinesToInputs,
    seedLotLine,
    seedMatLine,
} from '@/components/sections/lot-allocation-editor'
import { salesOrderApi } from '@/lib/api/sales-order-api'

/** Một mặt hàng đang thiếu, kèm ĐƠN VỊ của chính nó. */
export interface PosLotDebtLine {
    productName: string
    qty: number
    unit: string
}

export interface PosLotPayload {
    pos_lot_allocations: PosLotAllocationInput[]
    pos_material_lots: StockOutMaterialItemInput[]
    /** Thu ngân đã xác nhận bán nợ lô (FD-372). BE không tự sinh nợ nếu thiếu. */
    allow_lot_debt: boolean
}

/**
 * Chọn lô tại quầy: gọi BE gợi ý lô FIFO cho giỏ hàng hiện tại, seed vào
 * editor dùng chung với popup xuất kho, cho thu ngân đổi lô nếu khách lấy lô
 * khác trên kệ. Re-seed khi SL/SP/vật tư trong giỏ đổi (chỉnh tay bị bỏ — SL
 * đã khác thì phân bổ cũ không còn đúng).
 *
 * `allowDebt` phải khớp với ô "bán nợ lô" mà màn hình thực sự render: chỉ máy
 * POS bật cờ này. Màn không cho bán nợ mà vẫn gửi cờ thì đơn sinh nợ lô trong
 * khi người bán không hề thấy cảnh báo nào.
 */
export function usePosLotAllocation(
    tab: OrderTab,
    enabled: boolean,
    allowDebt = false,
) {
    const items = useMemo(
        () => tab.items.filter((i) => i.quantity > 0),
        [tab.items],
    )
    const materials = useMemo(
        () => (tab.packagingItems ?? []).filter((p) => p.quantity > 0),
        [tab.packagingItems],
    )
    // Chữ ký giỏ hàng — đổi là preview lại + seed lại.
    const signature = useMemo(
        () =>
            JSON.stringify({
                i: items.map((i) => [
                    i.product_id,
                    i.quantity,
                    i.discount_percent,
                ]),
                m: materials.map((p) => [p.packaging_material_id, p.quantity]),
            }),
        [items, materials],
    )
    const hasCart = items.length > 0 || materials.length > 0

    const query = useQuery({
        enabled: enabled && hasCart,
        queryKey: ['pos-stock-out-preview', signature],
        queryFn: async () => {
            const req: StockOutPreviewRequest = {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    quantity: i.quantity,
                    discount_percent: i.discount_percent,
                })),
                materials: materials.map((p) => ({
                    packaging_material_id: p.packaging_material_id,
                    quantity: p.quantity,
                })),
            }
            const res = await salesOrderApi.stockOutPreview(req)
            return res.data ?? null
        },
    })

    const [lines, setLines] = useState<LotLine[]>([])
    const [matLines, setMatLines] = useState<MatLine[]>([])
    // M-8: thiếu hàng là chuyện thường trực ở quầy nên "bán nợ lô" TICK SẴN;
    // cái được lưu là lần thu ngân chủ động BỎ tick, gắn với đúng chữ ký giỏ
    // lúc đó. Đổi giỏ (số lượng/mặt hàng) là quay về mặc định tick sẵn.
    const [debtOptOutFor, setDebtOptOutFor] = useState('')
    const seededFor = useRef('')

    useEffect(() => {
        if (!hasCart) {
            seededFor.current = ''
            setLines([])
            setMatLines([])
            return
        }
        const data = query.data
        if (!data || seededFor.current === signature) return
        seededFor.current = signature
        // BE trả product/material theo id; tên + đơn vị lấy từ giỏ hàng.
        const itemByProduct = new Map(items.map((i) => [i.product_id, i]))
        const matByID = new Map(
            materials.map((p) => [p.packaging_material_id, p]),
        )
        setLines(
            data.items.map((it) => {
                const src = itemByProduct.get(it.product_id)
                return seedLotLine({
                    ...it,
                    order_item_id: '',
                    product_name: src?.product_name ?? it.product_name,
                    unit: src?.unit ?? it.unit,
                })
            }),
        )
        setMatLines(
            (data.materials ?? []).map((mt) => {
                const src = matByID.get(mt.packaging_material_id)
                return seedMatLine({
                    ...mt,
                    material_name: src?.material_name ?? mt.material_name,
                    material_sku: src?.material_sku ?? mt.material_sku,
                    unit: src?.material_unit ?? mt.unit,
                })
            }),
        )
    }, [hasCart, query.data, signature, items, materials])

    // Phần thiếu theo TỪNG mặt hàng. Không cộng thành một số tổng: giỏ thiếu 3
    // gói chè và 4 cái bình mà báo "thiếu 7 gói" thì vừa sai số vừa sai đơn vị.
    const debtLines = useMemo<PosLotDebtLine[]>(
        () =>
            lines
                .map((l) => ({
                    productName: l.productName,
                    qty: lineShortfall(l),
                    unit: l.unit,
                }))
                .filter((d) => d.qty > 0),
        [lines],
    )
    const debtQty = useMemo(
        () => debtLines.reduce((sum, d) => sum + d.qty, 0),
        [debtLines],
    )

    // Tick sẵn, trừ khi thu ngân đã bỏ tick cho chính giỏ này.
    const debtConfirmed = allowDebt && debtOptOutFor !== signature
    const setDebtConfirmed = useCallback(
        (v: boolean) => setDebtOptOutFor(v ? '' : signature),
        [signature],
    )

    // Phủ thiếu chỉ hết là lỗi khi thu ngân ĐÃ tick xác nhận cho chính giỏ này.
    // Chưa tick thì vẫn là lỗi — nhưng lỗi này KHÔNG được dùng để disable nút.
    const hasError = hasAnyError(lines, debtConfirmed) || hasMatError(matLines)

    const buildPayload = (): PosLotPayload => ({
        pos_lot_allocations: lines.map((l) => ({
            product_id: l.productId,
            // Bỏ ô trống: sản phẩm hết sạch hàng gửi mảng rỗng, cả dòng thành nợ.
            allocations: l.rows
                .filter((r) => r.lotId && Number(r.qty) > 0)
                .map((r) => ({
                    product_lot_id: r.lotId,
                    quantity: Number(r.qty),
                })),
        })),
        pos_material_lots: matLinesToInputs(matLines),
        allow_lot_debt: allowDebt && debtQty > 0 && debtConfirmed,
    })

    return {
        lines,
        matLines,
        setLines,
        setMatLines,
        warehouseId: query.data?.warehouse_id,
        isLoading: hasCart && query.isLoading,
        isEmpty: lines.length === 0 && matLines.length === 0,
        hasError,
        debtLines,
        debtQty,
        debtConfirmed,
        setDebtConfirmed,
        buildPayload,
    }
}
